<template>
  <b-container fluid class="general-tab">
    <process-handler-modal
        v-model="showProcessHandlerModal"
        @submit-process-request="reprocessPendingSamples"
    >
    </process-handler-modal>
    <b-row
      align-v="center"
      align-h="center"
      class="general-tab__list-aggregator text-center no-internal-print"
    >
      <list-aggregator
        modal-item-id-key="id"
        :modal-table-items="selectedSamples"
        :modal-title="assetModule.name"
        :modal-table-header="aggregatorHeaders"
        :modal-item-name-key="['sampleNumber', 'lot']"
        :configuration-mode="true"
        @update-selection="updateSelection"
      />
    </b-row>
    <div class="general-tab__buttons mb-2 h-100 no-internal-print">
      <span>
         <b-button
           variant="primary"
           data-test="reprocess-btn"
           class="general-tab__reprocess-btn general-tab__generic-btn"
           :disabled="!hasPendingSamples"
           @click="openConfirmationModal"
         >
        {{ $t('engineering.reprocess-pending-samples') }}
      </b-button>
      </span>
    </div>
    <b-row class="no-internal-print" v-if="table && !!table.length">
      <b-col cols="12">
        <sample-table
          :table="table"
          :selected-samples="selectedSamples"
          :selected-samples-ids="selectedSamplesIds"
          :select-all-handler="selectAllHandler"
          :loadingDelete="loadingDelete"
          @select-all="selectAll"
          @toggle-item-selection="toggleItemSelection"
          @delete="deleteSamplesConfirmation"
          @edit-sample="goToEditSample"
        />
      </b-col>
    </b-row>
    <b-row class="no-internal-print" v-if="table && !!table.length">
      <b-col cols="12">
        <grid-paginator
          :per-page="pagination.pageSize"
          :total-rows="pagination.totalRows"
          :value="pagination.page"
          @input="paginationPageChange"
          class="general-tab__paginator pt-3"
        />
      </b-col>
    </b-row>
    <b-row class="no-internal-print" align-h="center" v-else>
      <s-empty-list
        :loading="loading"
      />
    </b-row>
    <b-row class="mt-5">
      <b-col cols="12">
        <label class="text-success">
          {{ $t('engineering.evolution') }}
        </label>
      </b-col>
      <b-col cols="3" class="text-left pt-3 pr-0">
        <b-form-group>
          <b-form-radio-group
            buttons
            button-variant="outline-primary"
            size="sm"
            v-model="selected"
            :options="reportTypes"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col
        class="general-tab__chart-wrapper"
        cols="12"
      >
        <s-loader
          v-if="loading"
          size="lg"
        />
        <default-chart
          v-else-if="chartDataHandler && !!chartDataHandler.length"
          ref="lineChart"
          data-test="line-chart"
          x-data-manager="dateTime"
          legend-icon="rect"
          :time-zone="timeZoneUTC"
          :key="selected"
          :chart-id="`chromatography-${selected}`"
          :chart-data="chartDataHandler"
          :loading="loadingChart"
          :remove-outlier.sync="removeChartOutlier"
          :enable-multi-axis.sync="enableChartMultiAxis"
          @toggle-outlier="getEvolutionChart(assetModule.id, true)"
          @toggle-multi-axis="getEvolutionChart(assetModule.id, true)"
        />
        <s-empty-list v-else/>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import GeneralTabService from '@/domain/services/installation/engineering/chromatography/generalTab';
import eventBus from '@/services/globalEventBus';
import engineeringEvents from '@/domain/events/engineering';
import ListAggregator from '@/components/listAggregator/index';
import GridPaginator from '@/components/lists/Paginator';
import DefaultChart from '@/components/charts/DefaultChart';
import tabNames from '@/domain/enums/installation/engineering/chromatography/chromatographyTabs';
import ProcessHandlerModal from '@/domain/components/installation/engineering/ProcessHandlerModal';
import LoadingMixin, { build } from '@/services/loadingMixin';
import SampleTable from '@/domain/components/installation/engineering/chromatography/general/SampleTable';
import defaultChartMixin from '@/domain/mixins/defaultChartMixin';
import {
  formatter as dateTimeFormatter,
} from '@/services/dateTime/datetimeService';
import {
  formats,
  timezones,
} from '@/constants/localizer';

export default {
  name: 'GeneralTab',
  props: {
    assetModule: {
      type: Object,
    },
  },
  components: {
    SampleTable,
    DefaultChart,
    GridPaginator,
    ListAggregator,
    ProcessHandlerModal,
  },
  mixins: [
    LoadingMixin,
    defaultChartMixin,
    build('delete'),
  ],
  data: () => ({
    table: [],
    selected: 'gases',
    selectedSamples: [],
    chart: {
      gases: [],
      rates: [],
    },
    pagination: {
      totalRows: null,
      pageSize: 10,
      page: 1,
    },
    amountOfPendingSamples: 0,
    amountOfPendingLots: 0,
    showProcessHandlerModal: false,
    timeZoneUTC: timezones.UTC,
  }),
  computed: {
    aggregatorHeaders() {
      return [
        this.$t('fields.engineering.chromatography.analysis.sample-number'),
        this.$t('fields.engineering.chromatography.analysis.lot-number'),
      ];
    },
    selectAllHandler() {
      return this.table.every(tableItem => this.selectedSamples.some(
        selectedSample => tableItem.id === selectedSample.id,
      ));
    },
    chartDataHandler() {
      const { gases, rates } = this.chart;
      const charts = {
        gases,
        rates,
      };
      return charts[this.selected];
    },
    reportTypes() {
      return [
        {
          text: this.$t('engineering.gases'),
          value: 'gases',
        },
        {
          text: this.$t('engineering.rates'),
          value: 'rates',
        },
      ];
    },
    selectedSamplesIds() {
      return this.selectedSamples.map(selectedSample => selectedSample.id);
    },
    hasPendingSamples() {
      return this.amountOfPendingSamples > 0;
    },
    lastPage() {
      return Math.ceil(this.pagination.totalRows / this.pagination.pageSize);
    },
  },
  methods: {
    updateSelection(updatedSelectedSamples) {
      this.selectedSamples = updatedSelectedSamples;
    },
    selectAll(selectAllValue) {
      if (selectAllValue) {
        this.table.forEach(item => this.addSelectedItem(item));
        return;
      }
      this.table.forEach(item => this.removeSelectedItem(item));
    },
    addSelectedItem(selectedItem) {
      const itemExists = this.selectedSamples.some(
        selectedSample => selectedSample.id === selectedItem.id,
      );
      if (itemExists) return;
      this.selectedSamples.push(selectedItem);
    },
    removeSelectedItem(selectedItem) {
      const itemIndex = this.selectedSamples.findIndex(
        (selectedSample => selectedSample.id === selectedItem.id),
      );
      this.selectedSamples.splice(itemIndex, 1);
    },
    toggleItemSelection(selection) {
      if (selection.selected) {
        this.addSelectedItem(selection.item);
        return;
      }
      this.removeSelectedItem(selection.item);
    },
    deleteSamplesConfirmation() {
      this.$confirmMessage({ code: 'MC034' })
        .accepted(() => this.deleteSamplesRequest());
    },
    deleteSamplesRequest() {
      const { assetModule, selectedSamplesIds } = this;
      this.startLoadingDelete();
      return GeneralTabService.deleteSample(assetModule.id, selectedSamplesIds)
        .then(() => {
          this.$pushAlert({ code: 'MS023' });
          this.$set(this.pagination, 'page', this.getPageNumberOnDelete(selectedSamplesIds));
          this.refreshData();
          this.updateSelection([]);
        })
        .catch((error) => {
          if (error.response.data && error.response.data.codigoMensagem === 'MA045') {
            this.$pushAlert({
              type: 'warning',
              title: this.$t('messages.MA045.title'),
              message: this.$t('messages.MA045.message'),
              messageList: error.response.data.parametros.amostras.map(sample => ({
                message: this.$t('messages.MA045.iterator', {
                  Lote: sample.lote,
                  DataHoraAmostra: this.dateMaskFormatterInUTC(sample.dataHoraAmostragem),
                }),
              })),
            });
          }
        })
        .finally(() => {
          this.endLoadingDelete();
          this.selectedSamples = [];
          this.refreshData();
        });
    },
    getPageNumber() {
      return (this.$route.params || {}).sampleListPage || 1;
    },
    getPageNumberOnDelete(selectedSamplesIds) {
      const currentPage = this.pagination.page;
      const currentPageIfLastSample = (currentPage - 1) || 1;

      const allSamplesFromPageDeleted = this.table
        && (this.table.length - selectedSamplesIds.length === 0);

      const isCurrentPageLast = currentPage === this.lastPage;

      return isCurrentPageLast && allSamplesFromPageDeleted ? currentPageIfLastSample : currentPage;
    },
    refreshData() {
      const { assetModule, pagination } = this;
      this.getSamples(assetModule.id, pagination);
      return this.getEvolutionChart(assetModule.id);
    },
    refreshPendingSamples() {
      this.$emit('change-pending-samples', this.amountOfPendingSamples);
    },
    refreshPendingLots() {
      this.$emit('change-pending-lots', this.amountOfPendingLots);
    },
    getSamples(assetModuleId, pagination) {
      this.startLoading();
      return GeneralTabService.getPagedSampleList(assetModuleId, pagination)
        .then((response) => {
          this.table = response.data;
          this.pagination.totalRows = response.total;
          this.amountOfPendingSamples = response.summary.amountOfPendingSamples;
          this.amountOfPendingLots = response.summary.amountOfPendingLots;
          this.refreshPendingSamples();
          this.refreshPendingLots();
        })
        .finally(() => {
          this.endLoading();
        });
    },
    paginationPageChange(page) {
      this.pagination.page = page;
      const { assetModule, pagination } = this;
      this.$emit('change-pagination', page);
      this.getSamples(assetModule.id, pagination);
    },
    getEvolutionChart(assetModuleId, loadingChart) {
      if (!loadingChart) {
        this.startLoading();
      }
      this.loadingChart = loadingChart;
      return GeneralTabService.getEvolutionCharts(assetModuleId, this.chartFilters)
        .then((response) => {
          this.chart = response;
        })
        .finally(() => {
          this.endLoading();
          this.loadingChart = false;
        });
    },
    goToEditSample() {
      this.$confirmMessage({ code: 'MC036' }).accepted(() => {
        const { id } = this.selectedSamples[0];
        this.$router.push({
          name: 'monitoring.engineering.module',
          params: {
            sampleListPage: this.pagination.page,
          },
          query: {
            tab: tabNames.SAMPLE,
            sampleId: id,
          },
        });
      });
    },
    openConfirmationModal() {
      this.showProcessHandlerModal = true;
    },
    reprocessPendingSamples(username) {
      this.startLoading();
      const messageCodeDefault = 'MS022';
      const messageCodeWithoutPer017 = 'MS040';
      let messageCode = messageCodeDefault;
      if (!this.$hasUserPermission('PER017')) {
        messageCode = messageCodeWithoutPer017;
      }
      GeneralTabService.reprocessPendingSamples(this.assetModule.id, username)
        .then(() => {
          this.$set(this.pagination, 'page', this.getPageNumber());
          this.refreshData();
          this.$pushAlert({ code: messageCode });
          this.showProcessHandlerModal = false;
          this.amountOfPendingSamples = 0;
          this.amountOfPendingLots = 0;
          this.refreshPendingSamples();
          this.refreshPendingLots();
        })
        .finally(() => {
          this.endLoading();
        });
    },
    init() {
      this.$set(this.pagination, 'page', this.getPageNumber());
      this.refreshData();
      eventBus.on(engineeringEvents.refreshData, this.refreshDataEvent, this);
    },
    deinit() {
      eventBus.off(engineeringEvents.refreshData, this.refreshDataEvent, this);
    },
    refreshDataEvent({ wait }) {
      wait(this.refreshData());
    },
    dateMaskFormatterInUTC(date) {
      if (!date) {
        return false;
      }
      return dateTimeFormatter(date, formats.DATETIME_SHORT_WITH_SECONDS, this.timeZoneUTC);
    },
  },
  created() {
    this.init();
  },
  beforeDestroy() {
    this.deinit();
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/mobile-first"
  .general-tab
    &__chart-wrapper
      +media-xs
        padding: 0
    &__list-aggregator
      min-height: 28px
    &__generic-btn
      min-height: unset !important
      min-width: unset !important
  ::v-deep
    .echarts
      textarea
        max-height: 98% !important
      div:last-child
        div
          margin-right: 0 !important
</style>
